import InlineMessage from '@ingka/inline-message';
import Text from '@ingka/text';
import { Type } from '@sinclair/typebox';
import { merge } from 'lodash';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from '../../../LotteryForm.module.scss';
import { ControlledInputField } from '../../../components/controlled-input-field/ControlledInputField';
import { ControlledDropdown } from '../../../components/controlled-dropdown/ControlledDropdown';
import { useFormModule } from '../../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../../types';
import { SwipePeacefulForm, SwipePeacefulFormTransformer } from './mode-forms/SwipePeacefulForm';
import type { SwipePeacefulFormType } from './mode-forms/SwipePeacefulForm';
import type { SwipeNormalFormType } from './mode-forms/SwipeNormalForm';
import { SwipeNormalForm, SwipeRulesNormalFormTransformer } from './mode-forms/SwipeNormalForm';

export type SwipeRulesPartialFormType = {
  timePerQuestion: string;
  mode: 'NORMAL' | 'PEACEFUL';
} & SwipePeacefulFormType &
  SwipeNormalFormType;

export const SwipeRulesPartialFormSchema = Type.Object(
  {
    timePerQuestion: Type.Integer({
      minimum: 10,
      maximum: 20,
      errorMessage: {
        type: 'form-error-message-numeric-values',
        minimum: 'form-error-message-minimum-numeric-value',
        maximum: 'form-error-message-maximum-numeric-value',
      },
    }),
    mode: Type.Union([Type.Literal('NORMAL'), Type.Literal('PEACEFUL')]),
  },
  { additionalProperties: true, $id: 'SwipeFormSchema' }
);

export const SwipeRulesPartialFormTransformer: ModuleTransformer<SwipeRulesPartialFormType> = {
  fromPayload: (args) => {
    return merge(
      SwipeRulesNormalFormTransformer.fromPayload(args),
      SwipePeacefulFormTransformer.fromPayload(args),
      {
        timePerQuestion: '' + (args.lottery.swipe?.config?.timePerQuestion ?? 0),
        mode: args.lottery.swipe?.config?.modeData.mode ?? 'NORMAL',
      }
    );
  },
  toPayload: (formData) => {
    let mergeTransformer;
    if (formData.mode === 'PEACEFUL') {
      mergeTransformer = SwipePeacefulFormTransformer.toPayload(formData);
    } else {
      mergeTransformer = SwipeRulesNormalFormTransformer.toPayload(formData);
    }
    return merge(mergeTransformer, {
      lottery: {
        swipe: {
          config: {
            timePerQuestion: parseInt(formData.timePerQuestion, 10),
            modeData: {
              mode: formData.mode,
            },
          },
        },
      },
    });
  },
};
type onValidationFunction = (moduleName: string, isValid: boolean) => void;

export type SwipeRulesPartialFormProps = Omit<
  FormModuleProps<SwipeRulesPartialFormType>,
  'onValidation'
> & {
  moduleName: string;
  setRequiredNumberOfQuestions: (requiredNumberOfQuestions: number) => void;
  onValidationList: [onValidationFunction, onValidationFunction];
};

export const SwipeRulesPartialForm: React.FC<SwipeRulesPartialFormProps> = ({
  moduleName,
  values,
  onChange,
  setRequiredNumberOfQuestions,
  templateId,
  initialValues,
  retailUnit,
  onValidationList,
  getFormState,
}) => {
  const { t } = useTranslation();

  const { control } = useFormModule({
    moduleName,
    defaultValues: values,
    schema: SwipeRulesPartialFormSchema,
    onChange,
    onValidation: onValidationList[0],
  });

  const mode = useWatch({
    control,
    name: 'mode',
    defaultValue: undefined,
  });

  return (
    <form>
      <InlineMessage
        className={styles['inline-message']}
        variant="informative"
        title={`${t('form-explanation-message-swipe-rules-title')}`}
        body={`${t('form-explanation-message-swipe-rules-body')}`}
      />

      <div className={styles['form-elements']}>
        <ControlledInputField
          control={control}
          labelKey="form-sign-up-swipe-config-time-per-question-label"
          name="timePerQuestion"
          type="number"
          suffix={`${t('general-seconds')}`}
          fieldHelper={{
            msg: `${SwipeRulesPartialFormSchema.properties.timePerQuestion.minimum}-${SwipeRulesPartialFormSchema.properties.timePerQuestion.maximum}`,
          }}
          showCharacterLimit={false}
          upperLimit={SwipeRulesPartialFormSchema.properties.timePerQuestion.maximum}
          lowerLimit={SwipeRulesPartialFormSchema.properties.timePerQuestion.minimum}
        />
      </div>
      <div className={styles['form-elements']}>
        <Text bodySize="m" className={styles['config-section']}>
          <b> {t('form-sign-up-swipe-config-mode-choice')}</b>
        </Text>
        <Text bodySize="m" className={styles['config-section']}>
          <b>{t('form-sign-up-swipe-config-mode-normal-label')}</b>
          {' - ' + t('form-sign-up-swipe-config-mode-normal-description')}
        </Text>
        <Text bodySize="m" className={styles['config-section']}>
          <b>{t('form-sign-up-swipe-config-mode-peaceful-label')}</b>
          {' - ' + t('form-sign-up-swipe-config-mode-peaceful-description')}
        </Text>

        <ControlledDropdown
          name="mode"
          control={control}
          id="lotta-swipe-mode-dropdown"
          label={`${t('swipe-mode-dropdown-label')}`}
          hintText={`${t('swipe-mode-dropdown-hint')}`}
          errorMessage={'swipe-mode-dropdown-error-message'}
          options={[
            { name: t('form-sign-up-swipe-config-mode-normal-label'), value: 'NORMAL' },
            { name: t('form-sign-up-swipe-config-mode-peaceful-label'), value: 'PEACEFUL' },
          ]}
        />
      </div>
      <div className={styles['form-elements']}>
        {mode === 'PEACEFUL' && (
          <SwipePeacefulForm
            values={values}
            onChange={onChange as (moduleName: string, data: Record<string, unknown>) => void}
            moduleName={moduleName}
            onValidation={onValidationList[1]}
            setRequiredNumberOfQuestions={setRequiredNumberOfQuestions}
            templateId={templateId}
            initialValues={initialValues}
            retailUnit={retailUnit}
            getFormState={getFormState}
          />
        )}
        {mode === 'NORMAL' && (
          <SwipeNormalForm
            values={values}
            onChange={onChange as (moduleName: string, data: Record<string, unknown>) => void}
            moduleName={moduleName}
            onValidation={onValidationList[1]}
            setRequiredNumberOfQuestions={setRequiredNumberOfQuestions}
            templateId={templateId}
            initialValues={initialValues}
            retailUnit={retailUnit}
            getFormState={getFormState}
          />
        )}
      </div>
    </form>
  );
};
